import * as React from 'react';
import type { EnvironmentKey } from '@web-config-app/core';
import { isConfigEntity } from '@web-config-app/core';
import type {
  OtherAuthoringEnvironmentCandidate,
  OtherAuthoringEnvironmentState,
  UseMultiEnvironmentEntityOperationsProps,
  BulkActionReportingStore,
} from '@web-config-app/core-react';
import {
  useMultiEnvironmentEntityOperations,
  useConfigEntity,
  useEntityStatusesByEnvironment,
} from '@web-config-app/core-react';
import { EntityDetailsProvider } from '@web-config-app/entity-form';

interface EntityDetailsMultiEnvironmentContainerProps
  extends Omit<
    React.ComponentProps<typeof EntityDetailsProvider>,
    'entityType' | 'instanceId' | 'configEntitySchema'
  > {
  // TODO:: Fix inconsistent typing of instanceId since it can be undefined
  // https://everlong.atlassian.net/browse/CACT-1600
  instanceId: string;
  multiEnvironmentOptions?: UseMultiEnvironmentEntityOperationsProps['options'];
  currentAuthoringEnvironment: EnvironmentKey;
  /**
   * Every authoring environment other than the current one. Statuses are looked up together
   * (see useEntityStatusesByEnvironment) so each gets its own one-click copy-or-sync action.
   */
  otherAuthoringEnvironments: OtherAuthoringEnvironmentCandidate[];
  children: React.ReactNode;
  tenantId: string;
  bulkActionReportingStore: BulkActionReportingStore;
}

/**
 * A container that provides additional multi-env context and data for entity details in a multi-environment setup.
 *
 * This component fetches the entity data for the selected promotion target (see
 * otherAuthoringEnvironments) using `useMultiEnvironmentEntityOperations` and provides it to
 * the `EntityDetailsProvider` through the `otherAuthoringEnvironmentState` prop.
 *
 */

export const EntityDetailsMultiEnvironmentContainer = ({
  domainEntity: entity,
  instanceId,
  entityRootData,
  entityMeta,
  setEntityData,
  operations,
  multiEnvironmentOptions,
  rootSchema,
  path,
  name,
  status,
  includedEntities,
  addIncludedEntity,
  setIncludedEntities,
  resetEntityData,
  children,
  currentAuthoringEnvironment,
  otherAuthoringEnvironments,
  isUsingConfigAppBackend,
  defaultEntityName,
  tenantId,
  bulkActionReportingStore,
}: React.PropsWithChildren<EntityDetailsMultiEnvironmentContainerProps>) => {
  const configEntity = useConfigEntity({
    domainEntity: entity,
    includeRelationships: true,
  });

  // Which candidate the *detailed* (with-includes) fetch points at. That payload is expensive
  // and only the sync modal needs it, so it covers one target at a time; the cheap status
  // lookups driving the menu cover every candidate.
  //
  // Holds only the author's explicit choice -- "auto-select when there's nothing to choose" is
  // derived below, so there's nothing to re-initialise when the candidate list changes.
  const [chosenTargetKey, setChosenTargetKey] = React.useState<
    EnvironmentKey | undefined
  >(undefined);

  // Re-derived each render rather than trusted as a standing flag: if the chosen target drops
  // out of the candidate list, isTargetSelected must go back to false rather than leave
  // entityData pointing at an environment that is no longer a target.
  const selectedCandidate =
    otherAuthoringEnvironments.find(
      (candidate) => candidate.environmentKey === chosenTargetKey,
    ) ??
    (otherAuthoringEnvironments.length === 1
      ? otherAuthoringEnvironments[0]
      : undefined);
  const isTargetSelected = selectedCandidate !== undefined;
  const selectedTarget = selectedCandidate ?? otherAuthoringEnvironments[0];

  const operationsEntity =
    entity.isUsingConfigAppBackend === false ? entity : configEntity;

  const candidateEnvironmentKeys = React.useMemo(
    () =>
      otherAuthoringEnvironments.map((candidate) => candidate.environmentKey),
    [otherAuthoringEnvironments],
  );

  // Read straight from React Query rather than lifted into state per candidate: the query
  // result is already the single source, and copying it into state added a second one.
  const candidateStatuses = useEntityStatusesByEnvironment({
    entity: isConfigEntity(operationsEntity)
      ? operationsEntity.domainEntity
      : operationsEntity,
    instanceId,
    environments: candidateEnvironmentKeys,
  });

  // These already describe only the selected target: useMultiEnvironmentEntityOperations
  // masks its own result when the query is disabled, so there's nothing to re-mask here.
  const {
    entityStatus,
    entityData,
    syncToEnvironment,
    readOnly,
    copyEntitiesToEnvironment,
    isEntityDataFetched,
  } = useMultiEnvironmentEntityOperations({
    entity: operationsEntity,
    options: {
      ...multiEnvironmentOptions,
      get: {
        ...multiEnvironmentOptions?.get,
        enabled:
          isTargetSelected && (multiEnvironmentOptions?.get?.enabled ?? true),
      },
    },
    entityData: entityRootData,
    instanceId,
    sourceEnvironment: currentAuthoringEnvironment,
    // Falls back to the current environment purely to keep the hook call unconditional when
    // there's no target at all; the query is disabled in that case (isTargetSelected).
    targetEnvironment:
      selectedTarget?.environmentKey ?? currentAuthoringEnvironment,
    targetEnvironmentName: selectedTarget?.name,
    isUsingConfigAppBackend,
    domainEntityType: entity.type,
    tenantId,
    bulkActionReportingStore,
  });

  // Every candidate's status comes from this one lookup, the selected target's included.
  // Reading that one from the detailed fetch saves a request but gives "does this entity
  // exist in X" two sources that disagree mid-refresh -- which produced a target flipping to
  // "Checking X..." on click, and a copied-to target re-offering "Copy to X".
  const candidatesWithStatus: OtherAuthoringEnvironmentCandidate[] =
    React.useMemo(
      () =>
        otherAuthoringEnvironments.map((candidate) => {
          const result = candidateStatuses[candidate.environmentKey];
          return {
            ...candidate,
            status: result?.status,
            readOnly: result?.readOnly,
            isStatusResolved: Boolean(result?.isResolved),
          };
        }),
      [otherAuthoringEnvironments, candidateStatuses],
    );

  // The target is an argument so a click acts in the same tick, rather than waiting a render
  // for the hooks to rebind. The choice is still recorded for the detailed fetch to follow.
  const copyToEnvironment = React.useCallback(
    (environmentKey: EnvironmentKey) => {
      setChosenTargetKey(environmentKey);
      const candidate = otherAuthoringEnvironments.find(
        (target) => target.environmentKey === environmentKey,
      );
      copyEntitiesToEnvironment({
        environmentKey,
        // A poor label, but better than reporting the copy against an empty name. The
        // details page always supplies a translated one.
        name: candidate?.name ?? environmentKey,
      });
    },
    [copyEntitiesToEnvironment, otherAuthoringEnvironments],
  );

  // The chosen target belongs to the entity it was chosen for, but this component survives a
  // route change to another entity -- the page isn't keyed by instance id. Reset during
  // render, not in an effect, so the next entity never shows a frame carrying the old choice.
  const [instanceIdInState, setInstanceIdInState] = React.useState(instanceId);
  if (instanceIdInState !== instanceId) {
    setInstanceIdInState(instanceId);
    setChosenTargetKey(undefined);
  }

  // Memoized because EntityDetailsProvider carries this into its context value's dependency
  // array -- a fresh identity re-renders the whole form tree. Undefined only when there are
  // no other environments at all, which the prop type permits.
  const otherAuthoringEnvironmentState:
    | OtherAuthoringEnvironmentState
    | undefined = React.useMemo(
    () =>
      selectedTarget
        ? {
            environmentKey: selectedTarget.environmentKey,
            entityStatus,
            entityData,
            syncToEnvironment,
            readOnly,
            copyToEnvironment,
            candidates: candidatesWithStatus,
            isTargetSelected,
            isTargetDataSettled: Boolean(isEntityDataFetched),
            selectTarget: setChosenTargetKey,
          }
        : undefined,
    [
      selectedTarget,
      entityStatus,
      entityData,
      syncToEnvironment,
      readOnly,
      copyToEnvironment,
      candidatesWithStatus,
      isTargetSelected,
      isEntityDataFetched,
    ],
  );

  return (
    <EntityDetailsProvider
      configEntitySchema={configEntity.schema}
      defaultEntityName={defaultEntityName}
      entityRootData={entityRootData}
      entityMeta={entityMeta}
      setEntityData={setEntityData}
      operations={operations}
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      otherAuthoringEnvironmentState={otherAuthoringEnvironmentState}
      rootSchema={rootSchema}
      path={path}
      name={name}
      status={status}
      instanceId={instanceId}
      entityType={entity.type}
      domainEntity={entity}
      includedEntities={includedEntities}
      addIncludedEntity={addIncludedEntity}
      setIncludedEntities={setIncludedEntities}
      resetEntityData={resetEntityData}
      isUsingConfigAppBackend={isUsingConfigAppBackend}
    >
      {children}
    </EntityDetailsProvider>
  );
};
